import type { ArticleCategoryResponse } from '@shared/api-utils';
import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { HorizontalScrollContainer } from '../../HorizontalScrollContainer/HorizontalScrollContainer';
import { PageHeader } from '../../PageHeader/PageHeader';
import { buildMagazinePageHref } from '../helpers/buildMagazinePageHref';
import { isMagazineCategory } from '../helpers/magazineCategory';

/** Figma magazine category pills: regular paragraph, 12px gap, py-2 / px-6. */
const MAGAZINE_CATEGORY_PILL_CLASSNAME =
  'inline-flex shrink-0 items-center justify-center rounded-full whitespace-nowrap paragraph px-6 py-2 outline-none focus-visible:ring-brand focus-visible:ring-[3px]';

const MAGAZINE_CATEGORY_PILL_VARIANT_CLASSNAME = {
  default: 'bg-button-background-inverse text-foreground hover:bg-button-background-inverse-hover',
  active: 'bg-button-background text-foreground-inverse hover:bg-button-background-hover',
} as const;

const MAGAZINE_HOME_PAGE_PREFIX = ROUTES.MAGAZINE.HOME_WITH_PAGE_NUMBER.replace(':pageNumber', '');

type MagazineHomeHeaderProps = {
  categories?: readonly ArticleCategoryResponse[];
  pathname?: string;
};

function getPillClassName(selected: boolean): string {
  return twMerge(MAGAZINE_CATEGORY_PILL_CLASSNAME, selected ? MAGAZINE_CATEGORY_PILL_VARIANT_CLASSNAME.active : MAGAZINE_CATEGORY_PILL_VARIANT_CLASSNAME.default);
}

function isBlogPath(pathname: string): boolean {
  return pathname === ROUTES.MAGAZINE.HOME || pathname.startsWith(MAGAZINE_HOME_PAGE_PREFIX);
}

function isCategoryActive(pathname: string, category: ArticleCategoryResponse): boolean {
  const categoryPath = buildMagazinePageHref({ type: 'category', categorySlug: category.slug }, 1);
  return pathname === categoryPath || pathname.startsWith(`${categoryPath}/page/`);
}

function MagazineHomeHeader({ categories = [], pathname = ROUTES.MAGAZINE.HOME }: MagazineHomeHeaderProps) {
  const t = useTranslations('magazine.home');
  const tCategories = useTranslations('magazine.categories');
  const isBlogActive = isBlogPath(pathname);

  return (
    <header className="flex flex-col gap-4 pb-5" data-testid="magazine-home-header">
      <PageHeader title={t('title')} />
      <nav aria-label={tCategories('nav_label')} data-testid="magazine-home-header-categories" data-track-container="Sub-navigation">
        <HorizontalScrollContainer className="gap-x-3">
          <Link
            href={ROUTES.MAGAZINE.HOME}
            aria-current={isBlogActive ? 'page' : undefined}
            data-testid="magazine-home-header-category-blog"
            data-track-name="Blog"
            className={getPillClassName(isBlogActive)}
          >
            {tCategories('blog')}
          </Link>
          {categories.filter(isMagazineCategory).map(category => {
            const active = isCategoryActive(pathname, category);

            return (
              <Link
                key={category.id}
                href={buildMagazinePageHref({ type: 'category', categorySlug: category.slug }, 1)}
                aria-current={active ? 'page' : undefined}
                data-testid={`magazine-home-header-category-${category.id}`}
                data-track-name={category.title}
                className={getPillClassName(active)}
              >
                {category.title}
              </Link>
            );
          })}
        </HorizontalScrollContainer>
      </nav>
    </header>
  );
}

export { MagazineHomeHeader, type MagazineHomeHeaderProps };
